import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface TenantAccessGuardProps {
  page?: string;
  feature?: string;
  children: React.ReactNode;
}

/**
 * Route-level tenant access guard.
 *
 * Hiding a navigation tab is not security, and a role check is not entitlement:
 * a user who types (or keeps) a direct URL must still be stopped when the
 * workspace has the module or page switched off. The backend answers 403 with a
 * stable code; this guard keeps the user from ever reaching a half-loaded page
 * in the first place.
 */
const TenantAccessGuard: React.FC<TenantAccessGuardProps> = ({ page, feature, children }) => {
  const { accessReady, canAccessPage, hasFeature, user } = useAuth();

  // No entitlement snapshot yet: show a stable shell instead of assuming access.
  if (!accessReady) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="tenant-access-loading">
        <div className="h-4 w-32 animate-pulse rounded bg-slate-200" />
        <div className="mt-4 h-3 w-full animate-pulse rounded bg-slate-100" />
        <div className="mt-2 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
          Loading workspace access
        </p>
      </div>
    );
  }

  const featureAllowed = !feature || hasFeature(feature);
  const pageAllowed = !page || canAccessPage(page);

  if (!featureAllowed || !pageAllowed) {
    const reason = !featureAllowed ? 'feature' : 'page';
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-8 shadow-sm" data-testid="tenant-access-denied">
        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-amber-700">
          Not available
        </p>
        <h1 className="mt-2 text-xl font-black text-slate-900">
          This page is not enabled for your workspace
        </h1>
        <p className="mt-3 text-sm text-slate-700">
          {reason === 'feature'
            ? `The module that owns this page is switched off for ${user?.tenant?.name || 'this workspace'}.`
            : `${user?.tenant?.name || 'This workspace'} has not granted you access to this page.`}
        </p>
        <p className="mt-2 text-sm text-slate-600">
          Your workspace administrator can request it from the platform owner. Nothing in your
          organisation was deleted — the data is simply hidden while the module is off.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/dashboard"
            className="inline-flex items-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"
          >
            Back to dashboard
          </Link>
          <Link
            to="/dashboard/profile"
            className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            My profile
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default TenantAccessGuard;
